import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

import {
  acceptTemplateShare,
  getTemplateShare,
  TemplateShareDetails,
} from '../../services/templates.service';
import { getTemplateDescriptionText } from './templateDescription';

export const SharedTemplatePage = () => {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [share, setShare] = useState<TemplateShareDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;

    const loadShare = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const nextShare = await getTemplateShare(token);
        if (isActive) {
          setShare(nextShare);
        }
      } catch (loadError) {
        if (isActive) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Unable to load this template invitation.'
          );
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    void loadShare();
    return () => {
      isActive = false;
    };
  }, [token]);

  const handleAccept = async () => {
    setIsAccepting(true);
    setError(null);

    try {
      await acceptTemplateShare(token);
      navigate('/templates?tab=my&shared=added', { replace: true });
    } catch (acceptError) {
      setError(acceptError instanceof Error ? acceptError.message : 'Unable to add this template.');
      setIsAccepting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: { xs: 3, sm: 4 }, border: '1px solid #e6e8ef', boxShadow: 'none' }}>
        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : null}
        {!isLoading && error && !share ? <Alert severity="error">{error}</Alert> : null}
        {!isLoading && share ? (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h4" sx={{ color: '#022b3a', fontWeight: 700 }}>
                {share.template.name || 'Untitled template'}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                Shared with you by {share.senderName}
              </Typography>
            </Box>
            <Typography sx={{ whiteSpace: 'pre-line' }}>
              {getTemplateDescriptionText(share.template.description) || 'No description added.'}
            </Typography>
            {error ? <Alert severity="error">{error}</Alert> : null}
            {share.status === 'accepted' ? (
              <Alert severity="success">You have already added this template.</Alert>
            ) : null}
            <Button
              variant="contained"
              size="large"
              disabled={isAccepting}
              onClick={
                share.status === 'accepted' ? () => navigate('/templates?tab=my') : handleAccept
              }
            >
              {share.status === 'accepted'
                ? 'View My templates'
                : isAccepting
                ? 'Adding...'
                : 'Add to My templates'}
            </Button>
          </Stack>
        ) : null}
      </Paper>
    </Container>
  );
};
